import React, { useRef } from 'react';

import type { WebappResponse } from '@eventbrite/event-listing-service-client';

import { ArtistsInfo, type ArtistInfo } from '@components/ArtistsInfo/ArtistsInfo';
import { Performers } from '@/components/Performers';
import { GoodToKnow } from '@/components/GoodToKnow/GoodToKnow';
import { ConversionBar } from '@components/ConversionBar';
import { EventDetails } from '@/components/EventDetails';
import { EventFAQs } from '@components/EventFAQs';
import { EventHero } from '@components/EventHero';
import { EventTags } from '@components/EventTags';
import { Footer } from '@components/Footer';
import { OrganizerCard } from '@components/OrganizerCard';
import { Overview } from '@/components/Overview';
import { RelatedEvents } from '@components/RelatedEvents';
import { MoreOrganizerEvents } from '@components/MoreOrganizerEvents';
import { ReportThisEventButton, type ReportThisEventButtonRef } from '@components/ReportThisEventButton';
import { EventSignalsBar } from '@/components/EventSignalsBar/EventSignalsBar';
import { Agenda } from '@/components/Agenda/Agenda';
import { Tab } from '@/components/Agenda/Content/Tab';

import { ViewDetailsButton } from '@shared/buttons/ViewDetailsButton/ViewDetailsButton';
import { Layout, Main } from '@shared/Layout/Layout';
import { SectionWrapper } from '@shared/SectionWrapper';
import { useAppContext } from '@shared/Wrapper';

import type { BasicInfo, Gallery, Tags, StructuredContent, Performers as PerformersType } from '@/types';
import type { OrganizerEventResponse } from '@/components/MoreOrganizerEvents/types';

import { useBodyRendering, useEndedEventDetailsVisibility, useTrackingPixels } from './hooks';
import { isEventEnded, getCarouselItems, getEventSlugFromBasicInfo, hasPerformers } from './utils/bodyUtils';
import { SalesEndedMessage } from './SalesEndedMessage/SalesEndedMessage';

import styles from './Body.module.scss';

import { Location } from '@/components/Location/Location';
import { useUserData } from '@/hooks/useUserData';
import { getLocationInfoFrom } from './utils/getLocationInfoFrom';
import { DisplayStatusProvider } from '@/components/_shared/contexts/DisplayStatusContext';
import { useListingsDesktopTwoRails } from '@/components/_shared/contexts/ExperimentsContext';
import { Stage } from '@eventbrite/webapp-constants';
import { NotificationBanner } from '../NotificationBanner';
import { OrganizerFollowStatusProvider } from '../_shared/contexts/FollowOrganizerContext';
import { OrganizerProfileProvider } from '../_shared/contexts/OrganizerProfileContext';
import { ToastProvider } from '@/components/_shared/contexts/ToastContext';
import { ConversionBarWrapper } from '../ConversionBar/ConversionBarWrapper/ConversionBarWrapper';

interface BodyProps {
  basicInfo: BasicInfo;
  gallery: Gallery;
  collections: WebappResponse['collections'];
  tags?: Tags;
  taxonomies?: WebappResponse['taxonomies'];
  structuredContent?: StructuredContent;
  artistInfo?: ArtistInfo;
  performers?: PerformersType;
  agendaInfo?: { tabs?: Tab[] };
  googleMapsApiKey: string;
  presenterInfo: WebappResponse['presenterInfo'];
  salesStatus?: WebappResponse['salesStatus'];
  stage: Stage;
  goodToKnow: WebappResponse['goodToKnow'];
  salesEndedMessageData?: WebappResponse['salesEndedMessageData'];
  notificationBannerOverride?: React.ReactNode;
  /** SSR-fetched organizer events; falls back to client lazy fetch when null. */
  initialMoreOrganizerEvents?: OrganizerEventResponse | null;
}

export const Body: React.FC<BodyProps> = ({
  basicInfo,
  collections,
  gallery,
  tags,
  taxonomies,
  performers,
  structuredContent,
  agendaInfo,
  artistInfo,
  googleMapsApiKey,
  presenterInfo,
  salesStatus,
  goodToKnow,
  stage,
  salesEndedMessageData,
  notificationBannerOverride,
  initialMoreOrganizerEvents,
}) => {
  const reportButtonRef = useRef<ReportThisEventButtonRef>(null);
  const {
    data: { locale },
  } = useAppContext();

  useTrackingPixels({ stage });

  // Custom hooks
  const { isBodyRendered } = useBodyRendering();
  const eventEnded = isEventEnded(basicInfo.status);
  const { showEndedEventDetails, expandDetails } = useEndedEventDetailsVisibility(eventEnded);
  const carouselItems = getCarouselItems(gallery);
  const { location, map } = getLocationInfoFrom(basicInfo, googleMapsApiKey);
  const eventSlug = getEventSlugFromBasicInfo(basicInfo);
  const { showAccessLink, userId, sessionId, guestId, isLoading } = useUserData();

  const isListingsDesktopTwoRails = useListingsDesktopTwoRails();

  const sessionData = isLoading ? undefined : { userId, sessionId, guestId };

  const enrichedLocation = showAccessLink && eventSlug ? { ...location, onlineEventUrl: `/x/${eventSlug}` } : location;

  const featuredVideo =
    gallery?.featuredVideo && gallery.featuredVideo.embedUrl
      ? { url: gallery.featuredVideo.url, embedUrl: gallery.featuredVideo.embedUrl }
      : undefined;

  const nativeVideo =
    gallery?.nativeVideo && gallery.nativeVideo.url
      ? { url: gallery.nativeVideo.url, thumbnail: gallery.nativeVideo.thumbnail || '' }
      : undefined;

  const salesEndedMessageComponent = salesEndedMessageData?.body ? (
    <SalesEndedMessage body={salesEndedMessageData?.body} vertical={basicInfo.vertical} />
  ) : null;

  const viewDetailsButton = eventEnded && !showEndedEventDetails ? <ViewDetailsButton onClick={expandDetails} /> : null;

  const overviewComponent = showEndedEventDetails ? (
    <Overview summary={basicInfo.summary} structuredContent={structuredContent} />
  ) : null;

  const artistInfoComponent =
    showEndedEventDetails && artistInfo ? (
      <div className={styles.artistsInfoContainer}>
        <ArtistsInfo info={artistInfo} />
      </div>
    ) : null;

  // Performers section (for organizations in the Music vertical only)
  const lineupWidget = structuredContent?.widgets?.lineup;
  const hasPerformersData = hasPerformers(performers);
  const shouldShowPerformers = showEndedEventDetails && hasPerformersData && !lineupWidget;

  const performersComponent = shouldShowPerformers ? (
    <Performers headliners={performers?.headliners ?? []} supporters={performers?.supporters ?? []} />
  ) : null;

  const goodToKnowComponent = showEndedEventDetails ? (
    <GoodToKnow highlights={goodToKnow?.highlights} refundPolicy={goodToKnow?.refundPolicy} />
  ) : null;

  const locationComponent = showEndedEventDetails ? <Location location={enrichedLocation} map={map} /> : null;
  const eventCategory = taxonomies?.category;
  const eventCity = basicInfo.venue?.address?.city;

  const agendaComponent = showEndedEventDetails && agendaInfo ? <Agenda tabs={agendaInfo?.tabs} /> : null;

  const faqsComponent = showEndedEventDetails ? <EventFAQs faqs={structuredContent?.widgets?.faqs ?? []} /> : null;

  const organizerCardComponent =
    showEndedEventDetails && basicInfo.id ? (
      <OrganizerCard organizer={basicInfo.organizer} eventId={basicInfo.id} />
    ) : null;

  const reportThisEventButtonComponent = showEndedEventDetails ? (
    <ReportThisEventButton
      ref={reportButtonRef}
      eventId={basicInfo.id}
      eventName={basicInfo.name}
      eventUrl={basicInfo.url}
      country={basicInfo.venue?.address?.country ?? 'US'}
      locale={locale}
    />
  ) : null;

  const relatedEventsComponent = basicInfo?.id ? (
    <RelatedEvents
      eventId={basicInfo.id}
      isBodyRendered={isBodyRendered}
      eventBasicInfo={basicInfo}
      sessionData={sessionData}
    />
  ) : null;

  const moreOrganizerEventsComponent =
    basicInfo?.id && basicInfo?.organizer?.id ? (
      <MoreOrganizerEvents
        eventId={basicInfo.id}
        isBodyRendered={isBodyRendered}
        eventBasicInfo={basicInfo}
        category={eventCategory}
        initialData={initialMoreOrganizerEvents ?? undefined}
      />
    ) : null;

  return (
    <DisplayStatusProvider salesStatusData={salesStatus} eventStatus={basicInfo.status}>
      <ToastProvider>
        <div
          className={styles.bodyWrapper}
          data-testid="body"
          data-listings-desktop-two-rails={isListingsDesktopTwoRails ? 'true' : undefined}
          id="listing-body"
        >
          <OrganizerFollowStatusProvider organizer={basicInfo.organizer}>
            <OrganizerProfileProvider organizer={basicInfo.organizer}>
              <div className={styles.heroAndBannerSection}>
                <NotificationBanner priorityBanner={notificationBannerOverride} />
                <EventHero
                  hasCroppedLogo={false}
                  mainBodyBackground={{ croppedLogoUrl50: gallery?.images?.[0]?.url }}
                  items={carouselItems}
                  featuredVideo={featuredVideo}
                  nativeVideo={nativeVideo}
                  eventName={basicInfo.name}
                />
              </div>
              <EventSignalsBar />
              <div className={styles.contentContainer}>
                <div className={styles.layoutWrapper}>
                  <Layout>
                    <Main className={styles.mainContent}>
                      <SectionWrapper name="salesEndedMessage">{salesEndedMessageComponent}</SectionWrapper>
                      <EventDetails basicInfo={basicInfo} collections={collections} presenterInfo={presenterInfo} />
                      {viewDetailsButton}
                      <SectionWrapper name="overview">{overviewComponent}</SectionWrapper>
                      <SectionWrapper name="performers">{performersComponent}</SectionWrapper>
                      <SectionWrapper name="artistsInfo">{artistInfoComponent}</SectionWrapper>
                      <SectionWrapper name="goodToKnow">{goodToKnowComponent}</SectionWrapper>
                      <SectionWrapper name="location">{locationComponent}</SectionWrapper>
                      <SectionWrapper name="agenda">{agendaComponent}</SectionWrapper>
                      <SectionWrapper name="faqs" noDivider>
                        {faqsComponent}
                      </SectionWrapper>
                      <SectionWrapper name="organizerCard" noDivider>
                        {organizerCardComponent}
                      </SectionWrapper>
                      <SectionWrapper name="reportThisEvent">{reportThisEventButtonComponent}</SectionWrapper>
                    </Main>
                    {moreOrganizerEventsComponent}
                    {relatedEventsComponent}
                    <EventTags tags={tags} city={eventCity} isOnline={basicInfo.isOnline} />
                  </Layout>
                </div>
                <ConversionBarWrapper>
                  <ConversionBar />
                </ConversionBarWrapper>
              </div>
              <Footer
                refTargets={{
                  'report-this-event-button': () => reportButtonRef.current?.triggerModal(),
                }}
              />
            </OrganizerProfileProvider>
          </OrganizerFollowStatusProvider>
        </div>
      </ToastProvider>
    </DisplayStatusProvider>
  );
};
